import type { AttributeFilters, date } from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import {
  type GlobalAuthState,
  type GlobalStateDispatch,
  type GlobalUserState,
  type SocketState,
  useGlobalStateContextSelectors,
} from 'components/GlobalState/GlobalStateModel';
import { selectApiScopeStringSet } from 'components/GlobalState/selectors/selectApiScopeStringSet';
import { selectMenuTree } from 'components/GlobalState/selectors/selectMenuTree';
import { useLocale } from 'containers/App/LocaleContext';
import type { ComponentType } from 'react';
import { useDispatch } from 'react-redux';
import {
  type Location,
  type NavigateFunction,
  type Params,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import type { Dispatch } from 'redux';
import type { AppTranslations } from 'translations/translations';
import { type ApiHook, useApi } from 'utils/request/useApi';
import type { AnyObject } from 'utils/types/AnyObject';

export interface AppProps {
  api: ApiHook;
  attributeFilters: AttributeFilters;
  auth: GlobalAuthState;
  dispatch: Dispatch<any>;
  globalStateDispatch: GlobalStateDispatch;
  location: Location;
  msg: AppTranslations;
  navigate: NavigateFunction;
  params: Readonly<Params<string>>;
  user: GlobalUserState;
  dateFormat: string;
  dateTimeFormat: string;
  timezone: date.Timezone;
  searchParams: URLSearchParams;
  socketState: SocketState;
}

export const withAppProps =
  <TProps extends AnyObject>(
    Component: ComponentType<TProps & AppProps>,
  ): ComponentType<TProps> =>
  (props) => {
    const api = useApi();
    const location = useLocation();
    const navigate = useNavigate();
    const params = useParams();
    const [searchParams] = useSearchParams();
    const msg = useLocale();
    const dispatch = useDispatch();
    const ctxProps = useGlobalStateContextSelectors({
      apiScopeStringSet: selectApiScopeStringSet,
      attributeFilters: prop('attributeFilters'),
      auth: prop('auth'),
      dateFormat: prop('dateFormat'),
      dateTimeFormat: prop('dateTimeFormat'),
      dispatch: prop('dispatch'),
      menuTree: selectMenuTree,
      socketState: prop('socketState'),
      user: prop('user'),
      timezone: prop('user', 'timezone'),
    });

    return (
      <Component
        api={api}
        apiScopeStringSet={ctxProps.apiScopeStringSet}
        attributeFilters={ctxProps.attributeFilters}
        auth={ctxProps.auth}
        dateFormat={ctxProps.dateFormat}
        dateTimeFormat={ctxProps.dateTimeFormat}
        dispatch={dispatch}
        globalStateDispatch={ctxProps.dispatch}
        location={location}
        menuTree={ctxProps.menuTree}
        msg={msg}
        navigate={navigate}
        params={params}
        searchParams={searchParams}
        socketState={ctxProps.socketState}
        timezone={ctxProps.timezone}
        user={ctxProps.user}
        {...props}
      />
    );
  };
